import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import api from '@/services/apiClient';
import type { Session } from '@supabase/supabase-js';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  /** Tenant-level channel-partner flag. Drives the conditional
   *  "Partners" link in the sidebar. Loaded after auth via
   *  `GET /api/profile`. Defaults to false while the fetch is in
   *  flight or if the user has no tenant row yet. */
  readonly isPartner: boolean;
  readonly tenantId: string | null;
}

interface AuthContextValue {
  readonly user: AuthUser | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly magicLinkSent: boolean;
  readonly sendSupabaseMagicLink: (email: string) => Promise<void>;
  readonly logout: () => Promise<void>;
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseSession, setSupabaseSession] = useState<Session | null>(null);
  const [supabaseReady, setSupabaseReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [profileFlags, setProfileFlags] = useState<{
    isPartner: boolean;
    tenantId: string | null;
  }>({ isPartner: false, tenantId: null });

  // Supabase auth state listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
      setSupabaseReady(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseSession(session);
      setSupabaseReady(true);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  // Once Supabase auth lands, fetch the tenant-level flags via
  // /api/profile. Best-effort — failure leaves the flags at their
  // default (false / null), which just means the user sees the
  // standard non-partner sidebar.
  const sessionUserId = supabaseSession?.user?.id ?? null;
  useEffect(() => {
    if (!sessionUserId) {
      setProfileFlags({ isPartner: false, tenantId: null });
      return;
    }
    const controller = new AbortController();
    api.profile
      .get(controller.signal)
      .then((p) => {
        setProfileFlags({ isPartner: p.is_partner, tenantId: p.tenant_id });
      })
      .catch(() => {
        // Silent — sidebar just doesn't show the Partners link
        setProfileFlags({ isPartner: false, tenantId: null });
      });
    return () => controller.abort();
  }, [sessionUserId]);

  const user = useMemo<AuthUser | null>(() => {
    if (supabaseSession?.user) {
      return {
        uid: supabaseSession.user.id,
        email: supabaseSession.user.email ?? null,
        isPartner: profileFlags.isPartner,
        tenantId: profileFlags.tenantId,
      };
    }
    return null;
  }, [supabaseSession, profileFlags]);

  const loading = !supabaseReady;

  const sendSupabaseMagicLink = useCallback(async (email: string) => {
    try {
      setError(null);
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: true,
          // Stamp the signup surface into user_metadata. The Supabase
          // auth email templates branch on {{ .Data.brand }} so ChefYoda
          // signups get ChefYoda copy and Meridian signups get Meridian
          // copy from ONE template (a project has one template per type).
          data: {
            brand: window.location.hostname.startsWith('chefyoda') ? 'chefyoda' : 'meridian',
          },
        },
      });
      if (otpError) throw otpError;
      setMagicLinkSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link');
    }
  }, []);

  const logoutFn = useCallback(async () => {
    setError(null);
    await supabase.auth.signOut().catch(console.error);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      magicLinkSent,
      sendSupabaseMagicLink,
      logout: logoutFn,
    }),
    [user, loading, error, magicLinkSent, sendSupabaseMagicLink, logoutFn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
